/**
 * Cancelled & Refunds — the closed bucket of the Service Ops page.
 *
 * Until now a cancelled or refund-requested job was in NO list, NO filter and
 * NO count on this page, so money already collected on a dead booking was
 * invisible. This table leads with the refund exposure: what we collected,
 * where the payment stands and the Razorpay payment id to refund against.
 */
import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Ban, ChevronRight, Copy, Loader2, Phone, MapPin, IndianRupee,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrencyAuto } from '@/utils/currency';
import { formatDateTime } from '@/utils/datetime';
import { statusBadge, statusLabel } from './useStatusCatalogue';

const fmtINR = (v) => (v == null ? '—' : formatCurrencyAuto(Math.round(Number(v))));

// What we actually collected. `paid_amount` is the field the Razorpay
// reconciler writes; `amount_paid` is the older manual-override field.
export const collectedAmount = (lead) => {
  const v = lead?.paid_amount ?? lead?.amount_paid;
  return v == null ? null : Number(v);
};

const PAYMENT_TONE = {
  PAID: 'bg-green-100 text-green-700 border-green-200',
  ADVANCE_PAID: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  PARTIAL_PAID: 'bg-amber-100 text-amber-800 border-amber-300',
  REFUNDED: 'bg-slate-100 text-slate-700 border-slate-200',
};

// Reason can come from three places: ops closure fields (new), the legacy
// free-text `cancellation_reason`, or the mechanic app's own rejection record.
const resolveReason = (lead, catalogue) => {
  if (lead?.closure_reason_label) {
    return {
      label: lead.closure_reason_label,
      note: lead.closure_reason_note || '',
      by: lead.closure_by_name,
      at: lead.closure_at,
      backfilled: !!lead.closure_reason_backfilled,
    };
  }
  if (lead?.status === 'MECHANIC_REJECTED' && lead?.rejection?.reason_code) {
    const map = catalogue?.mechanic_rejection_labels || {};
    return {
      label: map[lead.rejection.reason_code] || lead.rejection.reason_code,
      note: lead.rejection.reason_note || '',
      by: lead.rejection.by_mechanic_name,
      at: lead.rejection.at,
      backfilled: false,
    };
  }
  if (lead?.cancellation_reason) {
    return { label: lead.cancellation_reason, note: '', by: null, at: null, backfilled: false };
  }
  return null;
};

const lastStatusChange = (lead) => {
  const h = lead?.status_history || [];
  for (let i = h.length - 1; i >= 0; i -= 1) {
    if (h[i]?.to === lead?.status) return h[i];
  }
  return null;
};

export const CancelledRefundsTable = ({
  rows, total, loading, catalogue, onOpenLead, onAddReason,
}) => {
  const exposure = rows.reduce((sum, l) => sum + (collectedAmount(l) || 0), 0);
  const missingReasons = rows.filter((l) => !resolveReason(l, catalogue)).length;

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success('Copied');
  };

  return (
    <Card className="overflow-hidden" data-testid="cancelled-refunds-card">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 border-b bg-rose-50/60">
        <div className="flex items-center gap-2 text-rose-700">
          <IndianRupee className="h-4 w-4" />
          <span className="text-sm">
            Collected on cancelled / refund jobs:{' '}
            <span className="font-bold" data-testid="refund-exposure-total">{fmtINR(exposure)}</span>
          </span>
        </div>
        <div className="text-xs text-slate-600" data-testid="cancelled-count">
          {total} job{total === 1 ? '' : 's'}
        </div>
        {missingReasons > 0 && (
          <div className="text-xs font-medium text-amber-700" data-testid="missing-reason-count">
            {missingReasons} without a reason — add one so cancellations stay reportable
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cancelled-refunds-table">
          <thead className="bg-gray-50 border-b text-left">
            <tr>
              <th className="w-8 px-3 py-3" />
              <th className="px-3 py-3 font-medium text-gray-700">Customer</th>
              <th className="px-3 py-3 font-medium text-gray-700">Car / Package</th>
              <th className="px-3 py-3 font-medium text-gray-700">Status</th>
              <th className="px-3 py-3 font-medium text-gray-700">Reason</th>
              <th className="px-3 py-3 font-medium text-gray-700">Closed by</th>
              <th className="px-3 py-3 font-medium text-gray-700 text-right">Amount paid</th>
              <th className="px-3 py-3 font-medium text-gray-700">Payment</th>
              <th className="px-3 py-3 font-medium text-gray-700">Razorpay payment ID</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-gray-500 py-12">
                  <Ban className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <div className="font-medium">Nothing cancelled in this range</div>
                  <div className="text-xs mt-1">
                    Cancelled, mechanic-rejected and refund-requested jobs land here.
                  </div>
                </td>
              </tr>
            )}

            {rows.map((lead) => {
              const veh = lead.vehicle || {};
              const reason = resolveReason(lead, catalogue);
              const hist = lastStatusChange(lead);
              const paid = collectedAmount(lead);
              const rzp = lead.razorpay_payment_id;
              return (
                <tr
                  key={lead.id}
                  className="border-b hover:bg-rose-50/30 cursor-pointer align-top"
                  onClick={() => onOpenLead(lead)}
                  data-testid={`cancelled-row-${lead.id}`}
                >
                  <td className="px-3 py-3 text-gray-400"><ChevronRight className="h-4 w-4" /></td>
                  <td className="px-3 py-3">
                    <div className="font-semibold text-gray-900">{lead.customer_name || '—'}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <Phone className="h-3 w-3" />{lead.mobile}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-rose-500" />{lead.city || '—'}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-gray-900">
                      {[veh.brand, veh.model].filter(Boolean).join(' ') || '—'}
                    </div>
                    {veh.rc_number && (
                      <div className="text-[10px] font-mono uppercase text-gray-400 mt-0.5">
                        {veh.rc_number}
                      </div>
                    )}
                    <div className="text-xs text-gray-600 mt-0.5">{lead.package_name || '—'}</div>
                  </td>
                  <td className="px-3 py-3">
                    <Badge className={`${statusBadge(catalogue, lead.status)} border text-[10px] font-bold tracking-wide`}>
                      {statusLabel(catalogue, lead.status)}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 max-w-[240px]">
                    {reason ? (
                      <>
                        <div className="text-gray-900 font-medium">{reason.label}</div>
                        {reason.note && (
                          <div className="text-xs text-gray-500 mt-0.5 break-words">{reason.note}</div>
                        )}
                        {lead.status !== 'MECHANIC_REJECTED' && (
                          <button
                            className="text-[11px] text-blue-600 hover:underline mt-1"
                            onClick={(e) => { e.stopPropagation(); onAddReason(lead); }}
                            data-testid={`edit-reason-${lead.id}`}
                          >
                            Edit reason
                          </button>
                        )}
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                        onClick={(e) => { e.stopPropagation(); onAddReason(lead); }}
                        data-testid={`add-reason-${lead.id}`}
                      >
                        Add reason
                      </Button>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="text-gray-900">{reason?.by || hist?.by || '—'}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {formatDateTime(reason?.at || hist?.at || lead.updated_at)}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <div className={`font-semibold ${paid ? 'text-rose-700' : 'text-gray-400'}`}>
                      {paid ? fmtINR(paid) : '—'}
                    </div>
                    {lead.final_price != null && (
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        of {fmtINR(lead.final_price)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <Badge className={`${PAYMENT_TONE[lead.payment_status] || 'bg-gray-100 text-gray-600 border-gray-200'} border text-[10px] font-bold`}>
                      {lead.payment_status || 'PENDING'}
                    </Badge>
                  </td>
                  <td className="px-3 py-3">
                    {rzp ? (
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-xs text-gray-700">{rzp}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copy(rzp); }}
                          className="text-gray-400 hover:text-gray-700"
                          data-testid={`copy-rzp-${lead.id}`}
                          title="Copy payment id"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs italic text-gray-400">
                        {paid ? 'not reconciled' : 'no payment'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {loading && (
        <div className="flex items-center justify-center py-6 text-sm text-gray-500 gap-2 border-t">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
    </Card>
  );
};

export default CancelledRefundsTable;
